import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useNotificationStore } from '@/features/notifications/store/notificationStore';
import { useRecentStore } from '@/features/app-shell/store/recentStore';
import {
  ACCESS_TOKEN_KEY,
  SESSION_END_KEY,
  SESSION_END_EVENT,
  tokenService,
} from '@features/authentication/services/tokenService';

/**
 * Ends this tab's session when the session ends anywhere — and, just as importantly, does NOT end it
 * when a sibling tab merely has a bad half-second.
 *
 * 86d3zp17w. This used to key on ACCESS_TOKEN_KEY being REMOVED, which cannot distinguish a
 * deliberate sign-out from a tab that lost a refresh-token rotation race and gave up. Since all tabs
 * share one localStorage, the loser's cleanup ejected every other tab too — including tabs holding a
 * token that had just been refreshed successfully. That was the bulk of the reported "I came back to
 * the tab and I was logged out".
 *
 * Now the signal is POSITIVE: only a genuine end writes SESSION_END_KEY. Two channels, because a
 * `storage` event fires only in tabs OTHER than the one that made the change:
 *   - `storage`      → siblings
 *   - SESSION_END_EVENT → this tab
 */
export function useLogoutSync() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const end = (reason?: string) => {
      useAuthStore.setState({
        user: null,
        isAuthenticated: false,
        mustChangePassword: false,
      });

      // N7/N8 pre-deploy sweep, 2026-08-25: the notification store is APP-LIFETIME and logout is an
      // SPA navigation (the hard reload was deliberately removed — see the comment on
      // SESSION_END_EVENT), so module-scoped zustand state SURVIVES sign-out. Without this reset the
      // next user on the same tab inherits the previous user's badge counts, loaded rows and pinned
      // failures until a refetch happens to overwrite them — on the shared warehouse PCs this hook
      // exists for. This is the ONE choke point every session end passes through (user sign-out,
      // expiry, remote revocation, and the cross-tab storage broadcast), which is what makes it the
      // right home rather than the sign-out button.
      useNotificationStore.getState().reset();

      // M4 (86d44z223) — same reasoning, same choke point. The Recent list is server-side precisely
      // so it follows a user between shared warehouse terminals; leaving the previous user's cached
      // record NAMES in memory after sign-out would reintroduce exactly the leak that motivated
      // that choice.
      useRecentStore.getState().reset();

      // The marker has done its job; clear it so a later mount cannot re-trigger on a stale value.
      tokenService.clearSessionEnd();
      navigate('/login', {
        replace: true,
        state: {
          from: `${location.pathname}${location.search}`,
          reason: reason ?? 'expired',
        },
      });
    };

    const handleStorage = (event: StorageEvent) => {
      // Deliberate end, broadcast by another tab.
      if (event.key === SESSION_END_KEY && event.newValue !== null) {
        let reason: string | undefined;
        try {
          reason = JSON.parse(event.newValue)?.reason;
        } catch {
          /* fall through to the default */
        }
        end(reason);
        return;
      }

      // Straddle fallback — REMOVE ONE RELEASE AFTER 86d3zp17w SHIPS.
      // A tab still running the pre-fix bundle signs out by removing the access token and writes no
      // marker. Without this, that tab's Sign Out would leave this one rendering the full
      // authenticated UI — on a shared warehouse PC. No new-bundle path clears tokens outside
      // endSession(), so a bare removal can now only come from an old bundle or localStorage.clear().
      if (
        event.key === ACCESS_TOKEN_KEY &&
        event.newValue === null &&
        tokenService.readSessionEnd() === null
      ) {
        end('user-logout');
      }
    };

    const handleInTab = (event: Event) => {
      const reason = (event as CustomEvent<{ reason?: string }>).detail?.reason;
      end(reason);
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(SESSION_END_EVENT, handleInTab);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(SESSION_END_EVENT, handleInTab);
    };
  }, [navigate, location.pathname, location.search]);
}
